import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminForgot } from "@/lib/api";
import { toast } from "sonner";
import { Shield, ArrowRight, KeyRound } from "lucide-react";

const formatDetail = (d) => {
  if (!d) return "Login failed.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const forgot = async () => {
    if (!email) return toast.error("Please enter your admin email above first.");
    try {
      await adminForgot(email);
      toast.success("If an account exists, a reset instruction was prepared. Email delivery is currently inactive.");
      setShowForgot(false);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--gi-ivory)" }} data-testid="admin-login-page">
      <div className="hidden lg:flex flex-col justify-between p-16 flex-1 paper-noise" style={{ background: "linear-gradient(180deg, #6A1E25 0%, #4A1218 100%)", color: "var(--gi-ivory)" }}>
        <div className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>Glories of India Publications</div>
        <div>
          <div className="font-accent text-[10px] mb-6" style={{ color: "var(--gi-gold)" }}>Content Management</div>
          <h1 className="font-heading text-5xl leading-[1.05]">Publisher's<br />Editorial Desk</h1>
          <div className="hair-divider mt-8 max-w-xs" style={{ background: "linear-gradient(90deg, transparent, rgba(200,169,81,0.5) 20%, rgba(200,169,81,0.5) 80%, transparent)" }} />
          <p className="mt-8 max-w-md text-base leading-relaxed" style={{ color: "rgba(250,249,246,0.85)" }}>
            Manage books, editions, articles, videos, events and homepage content with editorial safety — every change is versioned, publication is owner-only.
          </p>
        </div>
        <div className="font-accent text-[10px]" style={{ color: "rgba(250,249,246,0.6)" }}>Restricted · Authorised Staff Only</div>
      </div>
      <div className="flex flex-1 items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-8">
            <Shield size={16} style={{ color: "var(--gi-maroon)" }} />
            <span className="font-accent text-[10px]" style={{ color: "var(--gi-maroon)" }}>Administrator Sign-in</span>
          </div>
          <h2 className="font-heading text-4xl leading-[1.05]" style={{ color: "var(--gi-maroon)" }}>Sign in to the Editorial Desk</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>Staff access only. Public readers do not have accounts.</p>
          <div className="mt-10 space-y-5">
            <div>
              <label className="gi-label">Email</label>
              <input className="gi-input" type="email" autoComplete="username" required value={email} onChange={e => setEmail(e.target.value)} data-testid="admin-login-email" />
            </div>
            <div>
              <label className="gi-label">Password</label>
              <input className="gi-input" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="admin-login-password" />
            </div>
            <button type="submit" disabled={busy} className="btn-maroon w-full justify-center" data-testid="admin-login-submit">
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
            </button>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <button type="button" onClick={() => setShowForgot(v => !v)} className="text-xs underline" style={{ color: "var(--gi-maroon)" }} data-testid="admin-forgot-toggle">
              <KeyRound size={12} className="inline mr-1" /> Forgot password?
            </button>
            <Link to="/" className="text-xs" style={{ color: "var(--gi-muted)" }}>Return to website</Link>
          </div>
          {showForgot && (
            <div className="mt-6 p-4 border" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }} data-testid="admin-forgot-block">
              <p className="text-xs" style={{ color: "var(--gi-charcoal-2)" }}>
                Enter the account email above and click Request. Outgoing email delivery is currently inactive — the reset token is logged to the server console until a verified email service is connected by the publisher.
              </p>
              <button type="button" onClick={forgot} className="btn-outline mt-3" data-testid="admin-forgot-submit">Request reset</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
